import React from 'react';
import { dayjs } from 'shared/utils/date';
import { Link } from 'react-router-dom';
import { IntlProvider, FormattedMessage, FormattedDate } from 'react-intl';
import globalMessages from 'translations/messages';
import { useQuery } from 'hooks/apollo';
import { formatMainDateRange } from 'shared/constants/date';

import NoMatch from 'containers/NoMatch';
import Loading from 'components/Loading';
import translations from 'translations';
import { Descriptions } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import Page from 'components/Page';
import ExtellioLogo from 'components/ExtellioLogo';
import Dashboard from 'components/Dashboard/Dashboard';
import messages from '../messages';

import './report.less';
import reportQuery from './reportQuery.gql';
import ClassicReportContent from './ClassicReportContent';

const ViewReport = ({ match, isPrint }) => {
  const dateParam = match.params.date && dayjs(match.params.date).utc(true);
  const viewDate = dateParam && dateParam.isValid()
    ? dateParam.toDate()
    : dayjs().tz('Europe/Stockholm').startOf('day').utc(true)
      .toDate();

  const id = match.params.reportId;
  const { loading, data: { report } = {} } = useQuery(reportQuery, {
    variables: { id, viewDate, format: isPrint ? 'PDF' : null },
  });

  if (loading || !report) {
    return (
      <IntlProvider locale="en-GB" key="en-GB" messages={translations['en-GB']}>
        {loading ? <Loading /> : <NoMatch />}
      </IntlProvider>
    );
  }

  const { customer } = report;
  const { title } = report;
  const distribution = (report.distributions.length && report.distributions[0]) || {};
  const { formattedTitle } = distribution;
  const isDashboard = !!report.dashboard;

  let breadcrumb;

  if (!isPrint) {
    breadcrumb = {
      items: [
        { path: '/reports', breadcrumbName: <FormattedMessage {...globalMessages.reports} /> },
        { path: `/reports/${report.id}/edit`, breadcrumbName: title },
      ],
      itemRender: (route, params, routes) => (
        routes.indexOf(route) === routes.length - 1
          ? route.breadcrumbName
          : <Link to={route.path}>{route.breadcrumbName}</Link>
      ),
    };
  }

  const header = (
    <PageHeader
      className="report__header"
      breadcrumb={breadcrumb}
      title={(
        <>
          {customer.logo && (
            <img
              className="report__header-logo"
              src={customer.logo.url}
              alt={customer.displayName}
            />
          )}
          {formattedTitle || title}
        </>
      )}
    >
      <Descriptions size="small" column={2}>
        <Descriptions.Item key="1" label={<FormattedMessage {...messages.reportCreated} />}>
          <FormattedDate value={viewDate} timeZone="UTC" weekday="long" day="numeric" month="long" year="numeric" />
        </Descriptions.Item>
        {!isDashboard && (
          <Descriptions.Item key="2" label={<FormattedMessage {...messages.dataCollected} />}>
            <FormattedMessage {...globalMessages.week}>
              {week => formatMainDateRange(dayjs, report.dateRange, viewDate, { week, locale: report.language })}
            </FormattedMessage>
          </Descriptions.Item>
        )}
      </Descriptions>

      {report.description && (
        <div className="report__header-description">
          {report.description}
        </div>
      )}
    </PageHeader>
  );

  let footer;
  if (isPrint) {
    footer = (
      <div className="report__footer">
        <ExtellioLogo />
      </div>
    );
  }

  return (
    <IntlProvider locale={report.language} key={report.language} messages={translations[report.language]}>
      {report.dashboard ? (
        <Dashboard dashboardId={report.dashboard.id} breadcrumb={breadcrumb} isReadOnly={isPrint} header={isPrint ? header : undefined} footer={footer} viewDate={viewDate} />
      ) : (
        <Page className="report">
          {header}
          <ClassicReportContent report={report} isPrint={isPrint} viewDate={viewDate} />
          {footer}
        </Page>
      )}
    </IntlProvider>
  );
};

export default ViewReport;
